import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Trophy, TrendingUp, MessageCircle, Briefcase, Users as UsersIcon,
  Clock, CheckCircle2, AlertTriangle, Phone, Mail, FileText, Calendar as CalIcon,
  ArrowLeft,
} from "lucide-react";
import { fmtINR, fmtDateTime, fmtDate } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

function initials(s) {
  if (!s) return "?";
  const parts = s.split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || s[0].toUpperCase();
}

function isoDay(d) { return d.toISOString().slice(0, 10); }

function defaultRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: isoDay(start), to: isoDay(now) };
}

const ACTIVITY_ICON = {
  meeting: { Icon: Briefcase, cls: "bg-primary-50 text-primary-700 border-primary-200" },
  message: { Icon: MessageCircle, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  call: { Icon: Phone, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  email: { Icon: Mail, cls: "bg-sky-50 text-sky-800 border-sky-200" },
  demo: { Icon: TrendingUp, cls: "bg-purple-50 text-purple-700 border-purple-200" },
  note: { Icon: FileText, cls: "bg-muted text-foreground border-border" },
  service_visit: { Icon: Briefcase, cls: "bg-orange-50 text-orange-700 border-orange-200" },
};

function Leaderboard({ data, onPickUser }) {
  const top3 = data.rows.slice(0, 3);
  const rest = data.rows.slice(3);
  const podiumColors = ["from-yellow-50 border-yellow-300", "from-zinc-50 border-zinc-300", "from-orange-50 border-orange-300"];
  const podiumLabels = ["1st", "2nd", "3rd"];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {top3.map((r, i) => (
          <button
            key={r.id}
            onClick={() => onPickUser(r.id)}
            data-testid={`podium-${i + 1}`}
            className={`p-3 sm:p-5 rounded-3xl border-2 bg-gradient-to-br to-card text-left tap card-lift overflow-hidden ${podiumColors[i]}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Trophy className={`h-4 w-4 ${i === 0 ? "text-yellow-600" : i === 1 ? "text-zinc-500" : "text-orange-600"}`} />
              <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{podiumLabels[i]}</span>
            </div>
            <p className="text-xs sm:text-sm font-bold truncate">{r.name || r.email}</p>
            <p className="font-display text-base sm:text-3xl font-black tracking-tight mt-1 sm:mt-2 truncate">{fmtINR(r.won_value)}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">{r.won_count} deal{r.won_count !== 1 ? "s" : ""}</p>
          </button>
        ))}
        {top3.length < 3 && Array.from({ length: 3 - top3.length }).map((_, i) => (
          <div key={`empty-${i}`} className="p-3 sm:p-5 rounded-3xl border-2 border-dashed border-border/60 text-center text-xs text-muted-foreground grid place-items-center">—</div>
        ))}
      </div>

      {rest.length > 0 && (
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-3 sm:p-5 space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 px-1">Everyone else</p>
            {rest.map((r, i) => (
              <button
                key={r.id}
                onClick={() => onPickUser(r.id)}
                data-testid={`leaderboard-row-${i + 4}`}
                className="w-full flex items-center gap-3 p-2.5 rounded-2xl hover:bg-muted/50 tap text-left"
              >
                <span className="font-mono text-xs text-muted-foreground w-6 shrink-0">#{i + 4}</span>
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary-100 text-primary-700 font-semibold text-xs">{initials(r.name || r.email)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{r.name || r.email}</p>
                  <p className="text-[11px] text-muted-foreground capitalize">{r.role} · {r.activities} activit{r.activities === 1 ? "y" : "ies"} · {r.followups_completed} follow-ups closed</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-base font-black">{fmtINR(r.won_value)}</p>
                  <p className="text-[10px] text-muted-foreground">{r.won_count} won</p>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ActivityFeed({ rows, users, filterUser, setFilterUser, filterType, setFilterType, onPickUser }) {
  const TYPES = ["", "meeting", "message", "call", "email", "demo", "note"];
  return (
    <div className="space-y-4">
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-2">
          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            data-testid="feed-user-filter"
            className="rounded-2xl h-10 px-3 text-sm border border-border bg-card focus:ring-2 focus:ring-primary"
          >
            <option value="">All employees</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            data-testid="feed-type-filter"
            className="rounded-2xl h-10 px-3 text-sm border border-border bg-card focus:ring-2 focus:ring-primary"
          >
            {TYPES.map((t) => <option key={t || "all"} value={t}>{t ? t.charAt(0).toUpperCase() + t.slice(1) : "All types"}</option>)}
          </select>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-2 sm:p-4 space-y-1.5">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6 text-center">No activity in this filter window.</p>
          ) : rows.map((r) => {
            const def = ACTIVITY_ICON[r.type] || ACTIVITY_ICON.note;
            const Icon = def.Icon;
            return (
              <div key={r.id} className="flex items-start gap-3 p-3 rounded-2xl border border-border/60" data-testid={`activity-${r.id}`}>
                <div className={`h-9 w-9 rounded-2xl grid place-items-center shrink-0 border ${def.cls}`}><Icon className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{r.summary}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    <button onClick={() => onPickUser(r.created_by)} className="underline hover:text-foreground tap">{r.user_name || "?"}</button>
                    {r.client_name ? <> · {r.client_name}</> : null}
                    {r.deal_title ? <> · {r.deal_title}</> : null}
                    {r.deal_value ? <> · {fmtINR(r.deal_value)}</> : null}
                  </p>
                  {r.preview && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{r.preview}</p>}
                </div>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">{fmtDateTime(r.created_at)}</span>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function UserSheet({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/admin/team/users/${userId}/summary`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl rounded-l-3xl p-0 overflow-y-auto" data-testid="user-detail-sheet">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/60">
          <SheetTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-primary" /> Employee snapshot
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">Last 30 days of activity, current pipeline, follow-up health.</SheetDescription>
        </SheetHeader>

        {loading || !data ? (
          <div className="p-5 space-y-4"><Skeleton className="h-24 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12"><AvatarFallback className="bg-primary-100 text-primary-700 font-semibold">{initials(data.user.name || data.user.email)}</AvatarFallback></Avatar>
              <div className="min-w-0">
                <p className="font-semibold truncate">{data.user.name || data.user.email}</p>
                <p className="text-xs text-muted-foreground">{data.user.email}</p>
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wider mt-0.5 capitalize">{data.user.role}</Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { l: "Won", v: fmtINR(data.stats.won_value), s: `${data.stats.won_count} deals` },
                { l: "Pipeline", v: fmtINR(data.stats.pipeline_value), s: `${data.stats.pipeline_count} open` },
                { l: "Activities (30d)", v: data.stats.activities_30d, s: "" },
                { l: "Overdue", v: data.stats.followups_overdue, s: `of ${data.stats.followups_pending} pending` },
              ].map((s, i) => (
                <div key={i} className="p-3 rounded-2xl border border-border/60 bg-muted/30">
                  <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">{s.l}</p>
                  <p className="font-display text-lg sm:text-xl font-black mt-1">{s.v}</p>
                  <p className="text-[10px] text-muted-foreground">{s.s}</p>
                </div>
              ))}
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Active deals</p>
              <div className="space-y-1.5">
                {data.deals.length === 0 ? <p className="text-sm text-muted-foreground italic px-1">No deals owned.</p> : data.deals.slice(0, 6).map((d) => (
                  <div key={d.id} className="p-2.5 rounded-2xl border border-border/60 flex items-center gap-3" data-testid={`user-deal-${d.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{d.title}</p>
                      <p className="text-[11px] text-muted-foreground capitalize">{(d.stage || "").replace(/_/g, " ")}</p>
                    </div>
                    <span className="font-mono text-xs font-semibold whitespace-nowrap">{fmtINR(d.value || 0)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Recent activity</p>
              <div className="space-y-1.5">
                {data.activities.length === 0 ? <p className="text-sm text-muted-foreground italic px-1">No activity in last 30 days.</p> : data.activities.slice(0, 12).map((a) => {
                  const def = ACTIVITY_ICON[a.type] || ACTIVITY_ICON.note;
                  const Icon = def.Icon;
                  return (
                    <div key={a.id} className="p-2.5 rounded-2xl border border-border/60 flex items-start gap-3" data-testid={`user-activity-${a.id}`}>
                      <div className={`h-7 w-7 rounded-xl grid place-items-center shrink-0 border ${def.cls}`}><Icon className="h-3.5 w-3.5" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{a.summary}</p>
                        <p className="text-[10px] text-muted-foreground">{fmtDateTime(a.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Team() {
  const [range, setRange] = useState(defaultRange);
  const [tab, setTab] = useState("leaderboard");
  const [board, setBoard] = useState(null);
  const [feed, setFeed] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("");
  const [filterType, setFilterType] = useState("");
  const [pickedUser, setPickedUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, u] = await Promise.all([
        api.get("/admin/team/leaderboard", { params: { from_date: new Date(range.from).toISOString(), to_date: new Date(range.to + "T23:59:59").toISOString() } }),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setBoard(b.data);
      setUsers((u.data || []).filter((x) => x.is_active));
    } finally { setLoading(false); }
  }, [range.from, range.to]);

  const loadFeed = useCallback(async () => {
    const params = {};
    if (filterUser) params.user_id = filterUser;
    if (filterType) params.type = filterType;
    const r = await api.get("/admin/team/activity", { params });
    setFeed(r.data || []);
  }, [filterUser, filterType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1180px] mx-auto">
      <header className="mb-6 space-y-1.5 animate-fade-in-up">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Admin · Team</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Sales pulse.</h1>
        <p className="text-sm text-muted-foreground">Who's closing, who's active, and what's stalled — in one glance.</p>
      </header>

      <Card className="rounded-3xl border-border/60 shadow-soft mb-5">
        <CardContent className="p-3 sm:p-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">From</Label>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="rounded-2xl h-10 mt-1 w-[160px]" data-testid="range-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">To</Label>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="rounded-2xl h-10 mt-1 w-[160px]" data-testid="range-to" />
          </div>
          <div className="flex flex-wrap gap-1.5 ml-auto">
            <Button size="sm" variant="outline" className="rounded-2xl tap" onClick={() => setRange(defaultRange())}>This month</Button>
            <Button size="sm" variant="outline" className="rounded-2xl tap" onClick={() => {
              const n = new Date(); const s = new Date(n); s.setDate(s.getDate() - 30);
              setRange({ from: isoDay(s), to: isoDay(n) });
            }}>Last 30d</Button>
            <Button size="sm" variant="outline" className="rounded-2xl tap" onClick={() => {
              const n = new Date(); const q = Math.floor(n.getMonth() / 3); const s = new Date(n.getFullYear(), q * 3, 1);
              setRange({ from: isoDay(s), to: isoDay(n) });
            }}>This quarter</Button>
          </div>
        </CardContent>
      </Card>

      {board && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
          <div className="p-3 sm:p-5 rounded-3xl border border-border/60 bg-card shadow-soft overflow-hidden">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground truncate">Total won</p>
            <p className="font-display text-base sm:text-3xl font-black mt-1 sm:mt-1.5 text-primary truncate">{fmtINR(board.total_won_value)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{board.total_won_count} deals</p>
          </div>
          <div className="p-3 sm:p-5 rounded-3xl border border-border/60 bg-card shadow-soft overflow-hidden">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground truncate">In pipeline</p>
            <p className="font-display text-base sm:text-3xl font-black mt-1 sm:mt-1.5 truncate">{fmtINR((board.rows || []).reduce((s, r) => s + (r.pipeline_value || 0), 0))}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{(board.rows || []).reduce((s, r) => s + (r.pipeline_count || 0), 0)} open</p>
          </div>
          <div className="p-3 sm:p-5 rounded-3xl border border-border/60 bg-card shadow-soft overflow-hidden">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground truncate">Activities</p>
            <p className="font-display text-base sm:text-3xl font-black mt-1 sm:mt-1.5">{(board.rows || []).reduce((s, r) => s + (r.activities || 0), 0)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">team total</p>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-2xl">
          <TabsTrigger value="leaderboard" className="rounded-xl" data-testid="team-tab-leaderboard"><Trophy className="h-3 w-3 mr-1" />Leaderboard</TabsTrigger>
          <TabsTrigger value="activity" className="rounded-xl" data-testid="team-tab-activity"><Clock className="h-3 w-3 mr-1" />Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="leaderboard" className="mt-4">
          {loading || !board ? <Skeleton className="h-64 rounded-3xl" /> : <Leaderboard data={board} onPickUser={setPickedUser} />}
        </TabsContent>
        <TabsContent value="activity" className="mt-4">
          <ActivityFeed
            rows={feed}
            users={users}
            filterUser={filterUser}
            setFilterUser={setFilterUser}
            filterType={filterType}
            setFilterType={setFilterType}
            onPickUser={setPickedUser}
          />
        </TabsContent>
      </Tabs>

      <UserSheet userId={pickedUser} onClose={() => setPickedUser(null)} />
    </div>
  );
}
